/**
 * Harvest maturity: a tree has reached its genus' target DBH (mýtní tloušťka). Defaults are
 * the ISLH 2017 table the prototype used; a report may override single genera (RP5).
 */
import { genusOf } from '@/map/layers';
import type { Threshold } from './config';
import type { Tree } from './types';

export interface Target {
  min: number;
  max: number;
}

export const ISLH_2017: Record<string, Target> = {
  Picea: { min: 45, max: 55 },
  Pinus: { min: 40, max: 50 },
  Larix: { min: 45, max: 55 },
  Abies: { min: 55, max: 70 },
  Fagus: { min: 50, max: 60 },
  Quercus: { min: 60, max: 75 },
  Fraxinus: { min: 45, max: 55 },
  Acer: { min: 45, max: 55 },
  Betula: { min: 30, max: 40 },
  Alnus: { min: 35, max: 45 },
  Populus: { min: 40, max: 50 },
  Pseudotsuga: { min: 60, max: 80 },
  Tilia: { min: 50, max: 60 },
  Prunus: { min: 40, max: 50 },
  Juglans: { min: 45, max: 60 },
  Robinia: { min: 30, max: 45 },
  Ulmus: { min: 50, max: 60 },
  Carpinus: { min: 35, max: 45 },
  Castanea: { min: 50, max: 65 },
  Salix: { min: 40, max: 50 },
  Sorbus: { min: 35, max: 45 },
};

/** A species genus the table does not know is mature from here. */
export const FALLBACK_MIN_DBH = 50;

export function thresholdsFor(overrides: Threshold[]): Record<string, Target> {
  const out = { ...ISLH_2017 };
  for (const o of overrides) out[genusOf(o.genus)] = { min: o.min, max: o.max };
  return out;
}

export function isMature(t: Tree, thresholds: Record<string, Target>): boolean {
  if (!t.isSpecies || t.dbhCm === null) return false;
  return t.dbhCm >= (thresholds[t.genus]?.min ?? FALLBACK_MIN_DBH);
}

export interface MaturityRow {
  genus: string;
  total: number;
  mature: number;
  pct: number;
  meanDbh: number | null;
  target: Target | null;
  overridden: boolean;
}

export interface MaturityStats {
  matureCount: number;
  /** Of all trees, the non-species classes included. */
  maturePct: number;
  matureVolume: number;
  rows: MaturityRow[];
}

export function maturity(trees: Tree[], overrides: Threshold[]): MaturityStats {
  const thresholds = thresholdsFor(overrides);
  const overridden = new Set(overrides.map((o) => genusOf(o.genus)));
  const groups = new Map<string, Tree[]>();
  let matureCount = 0;
  let matureVolume = 0;
  for (const t of trees) {
    if (isMature(t, thresholds)) {
      matureCount += 1;
      matureVolume += t.volume ?? 0;
    }
    if (t.isSpecies) {
      const list = groups.get(t.genus);
      if (list) list.push(t);
      else groups.set(t.genus, [t]);
    }
  }
  const rows = [...groups.entries()]
    .map(([genus, list]): MaturityRow => {
      const mature = list.filter((t) => isMature(t, thresholds)).length;
      const dbhs = list.map((t) => t.dbhCm).filter((v): v is number => v !== null);
      return {
        genus,
        total: list.length,
        mature,
        pct: (mature / list.length) * 100,
        meanDbh: dbhs.length ? dbhs.reduce((a, b) => a + b, 0) / dbhs.length : null,
        target: thresholds[genus] ?? null,
        overridden: overridden.has(genus),
      };
    })
    .sort((a, b) => b.total - a.total || a.genus.localeCompare(b.genus));
  return { matureCount, maturePct: trees.length ? (matureCount / trees.length) * 100 : 0, matureVolume, rows };
}
